import React, { useState, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faDownload, faShareAlt, faArrowUp } from "@fortawesome/free-solid-svg-icons"
import { usePWAInstall } from "../../../hooks/usePWAInstall"
import "./index.scss"

/**
 * PWA Install Banner Component
 *
 * Shows a smart install banner that:
 * - Detects platform and shows appropriate install method
 * - Can be dismissed (remembers for 7 days)
 * - Shows iOS-specific instructions modal
 * - Only appears for non-installed users
 */
const PWAInstallBanner = () => {
	const {
		isInstallable,
		isInstalled,
		isIOS,
		promptInstall,
		wasDismissedRecently,
		dismissBanner,
		getInstallInstructions,
	} = usePWAInstall()

	const [isDismissed, setIsDismissed] = useState(true) // Start hidden, show after check
	const [showIOSModal, setShowIOSModal] = useState(false)

	useEffect(() => {
		// Check if we should show the banner
		const shouldHide = wasDismissedRecently(7)
		setIsDismissed(shouldHide)
	}, [wasDismissedRecently])

	// Don't render if already installed
	if (isInstalled) return null

	// Don't render if dismissed
	if (isDismissed) return null

	// Don't render if not installable and not iOS
	// (iOS doesn't fire beforeinstallprompt but can still install via Safari)
	if (!isInstallable && !isIOS) return null

	const handleDismiss = () => {
		dismissBanner()
		setIsDismissed(true)
	}

	const handleInstallClick = async () => {
		if (isIOS) {
			// Show iOS-specific instructions
			setShowIOSModal(true)
		} else {
			// Use native install prompt
			const accepted = await promptInstall()
			if (accepted) {
				setIsDismissed(true)
			}
		}
	}

	const closeModal = () => setShowIOSModal(false)

	const handleOverlayClick = e => {
		if (e.target === e.currentTarget) {
			closeModal()
		}
	}

	const handleOverlayKeyDown = e => {
		if (e.key === "Escape") {
			closeModal()
		}
	}

	const instructions = getInstallInstructions()

	return (
		<>
			<div className="PWAInstallBanner">
				<div className="banner-content">
					<img src="/icons/icon-72x72.png" alt="Guerrilla Ed" className="app-icon" />
					<div className="text">
						<strong>Install Guerrilla Ed</strong>
						<span>Add to home screen for the best experience</span>
					</div>
				</div>
				<div className="banner-actions">
					<button type="button" className="install-btn" onClick={handleInstallClick} aria-label="Install app">
						<FontAwesomeIcon icon={isIOS ? faShareAlt : faDownload} />
						Install
					</button>
					<button type="button" className="dismiss-btn" onClick={handleDismiss} aria-label="Dismiss banner">
						<FontAwesomeIcon icon={faTimes} />
					</button>
				</div>
			</div>

			{/* iOS Instructions Modal */}
			{showIOSModal && (
				// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
				<div
					className="ios-modal-overlay"
					onClick={handleOverlayClick}
					onKeyDown={handleOverlayKeyDown}
					role="dialog"
					aria-modal="true"
					aria-labelledby="ios-modal-title"
					tabIndex={-1}
				>
					{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
					<div
						className="ios-modal"
						onClick={e => e.stopPropagation()}
						onKeyDown={e => e.stopPropagation()}
						role="document"
					>
						<button type="button" className="close-btn" onClick={closeModal} aria-label="Close">
							<FontAwesomeIcon icon={faTimes} />
						</button>

						<h2 id="ios-modal-title">Install on {instructions.platform}</h2>
						<p className="browser-note">
							Use <strong>{instructions.browser}</strong> to install
						</p>

						<ol className="install-steps">
							{instructions.steps.map((step, idx) => (
								// eslint-disable-next-line react/no-array-index-key
								<li key={`ios-step-${instructions.platform}-${idx}`}>
									{idx === 0 && isIOS && (
										<span className="step-icon">
											<FontAwesomeIcon icon={faArrowUp} />
										</span>
									)}
									{step}
								</li>
							))}
						</ol>

						<button type="button" className="got-it-btn" onClick={closeModal}>
							Got it!
						</button>
					</div>
				</div>
			)}
		</>
	)
}

export default PWAInstallBanner
